import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Box,
  Flex,
  Text,
  Button,
  Badge,
  Tag,
  Wrap,
  WrapItem,
  Spinner,
  Center,
  Divider,
  IconButton,
  Input,
  InputGroup,
  InputLeftAddon,
  InputLeftElement,
  Switch,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, MinusIcon, WarningIcon, SearchIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { splitMovement, fetchAccounts } from '../../api/endpointsBackend';
import { formatMoney } from '../../utils/formatters';

/**
 * Compact searchable account picker (matches CustomDropdown pattern from MovementDetailPanel)
 */
const AccountPicker = ({ value, options, onChange, placeholder }) => {
  const { t } = useTranslation('movements');
  const effectivePlaceholder = placeholder || t('modals.split.accountPicker.placeholder');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [search, setSearch] = useState('');

  const filtered = options.filter(opt =>
    opt.name.toLowerCase().includes(search.toLowerCase())
  );
  const selected = options.find(opt => String(opt.id) === String(value));

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-start">
      <PopoverTrigger>
        <Flex
          align="center"
          gap={1}
          cursor="pointer"
          bg="redesign.bg.card"
          px={2}
          py={1}
          borderRadius="md"
          border="1px solid"
          borderColor="redesign.border"
          _hover={{ borderColor: 'redesign.border' }}
          flex={1}
          minW={0}
        >
          <Text fontSize="xs" noOfLines={1} flex={1} color={selected ? 'redesign.heading' : 'redesign.text.muted'}>
            {selected ? selected.name : effectivePlaceholder}
          </Text>
          <ChevronDownIcon color="redesign.text.muted" boxSize={3} flexShrink={0} />
        </Flex>
      </PopoverTrigger>
      <PopoverContent w="220px" boxShadow="lg" borderRadius="lg">
        <PopoverBody p={0}>
          <Box p={2} borderBottom="1px solid" borderColor="redesign.border">
            <InputGroup size="xs">
              <InputLeftElement>
                <SearchIcon color="redesign.text.muted" boxSize={3} />
              </InputLeftElement>
              <Input
                placeholder={t('modals.split.accountPicker.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                variant="filled"
                borderRadius="md"
                fontSize="xs"
              />
            </InputGroup>
          </Box>
          <Box maxH="150px" overflowY="auto" py={1}>
            {filtered.map(opt => (
              <Flex
                key={opt.id}
                px={3}
                py={1.5}
                cursor="pointer"
                bg={String(opt.id) === String(value) ? 'redesign.bg.accent' : 'transparent'}
                _hover={{ bg: String(opt.id) === String(value) ? 'redesign.bg.accent' : 'redesign.bg.hover' }}
                onClick={() => {
                  onChange(String(opt.id));
                  onClose();
                  setSearch('');
                }}
              >
                <Text fontSize="xs" fontWeight={String(opt.id) === String(value) ? '600' : '400'}>
                  {opt.name}
                </Text>
              </Flex>
            ))}
            {filtered.length === 0 && (
              <Text fontSize="xs" color="redesign.text.muted" px={3} py={2}>{t('modals.split.accountPicker.noResults')}</Text>
            )}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Compact movement row for the preview list
 */
const MovementRow = ({ movement, index, highlight = false }) => {
  const { t } = useTranslation('movements');
  const isTransfer = movement.type === 'Transfer';
  const accountText = isTransfer
    ? `${movement.from_account?.name || '?'} → ${movement.to_account?.name || '?'}`
    : movement.account?.name || t('fallback.unknown');

  const tagNames = (movement.tag_movements || []).map(tm => tm.tag?.name).filter(Boolean);

  return (
    <Flex
      py={2}
      px={3}
      borderLeft="3px solid"
      borderLeftColor={highlight ? 'redesign.accent.green' : isTransfer ? 'redesign.text.muted' : 'redesign.accent.blue'}
      bg={highlight ? 'rgba(34, 197, 94, 0.10)' : 'redesign.bg.card'}
      align="center"
      mb="1px"
      borderRadius="sm"
    >
      <Box flex={1} minW={0}>
        <Flex align="center" gap={1}>
          <Text fontSize="xs" fontWeight="600" noOfLines={1}>
            {movement.note || movement.description || t('fallback.noNote')}
          </Text>
          {index !== undefined && (
            <Badge colorScheme={highlight ? 'green' : 'gray'} fontSize="2xs" variant="subtle">
              {t('modals.split.partBadge', { n: index + 1 })}
            </Badge>
          )}
          {isTransfer && (
            <Badge colorScheme="gray" fontSize="2xs" variant="outline">
              {t('modals.split.transferBadge')}
            </Badge>
          )}
        </Flex>
        <Flex align="center" gap={2}>
          <Text fontSize="2xs" color="redesign.text.muted" noOfLines={1}>
            {accountText}
          </Text>
          <Text fontSize="2xs" color="redesign.text.muted">
            {movement.date}
          </Text>
        </Flex>
        {tagNames.length > 0 && (
          <Wrap spacing={1} mt={1}>
            {tagNames.map((name, idx) => (
              <WrapItem key={idx}>
                <Tag size="sm" colorScheme="blue" variant="subtle" fontSize="2xs">
                  {name}
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        )}
      </Box>
      <Text
        fontSize="xs"
        fontWeight="600"
        ml={2}
        flexShrink={0}
        color={
          movement.type === 'Income' ? 'redesign.accent.green' :
          movement.type === 'Expenses' ? 'redesign.accent.red' :
          'redesign.text'
        }
      >
        {formatMoney(movement.amount, movement.currency)}
      </Text>
    </Flex>
  );
};

/**
 * Warning banner component (reused from MergeMovementsModal pattern)
 */
const WarningBanner = ({ warnings }) => {
  if (!warnings || warnings.length === 0) return null;

  return (
    <Box mb={3}>
      {warnings.map((w, idx) => {
        const isBlocking = ['too_few_parts', 'invalid_amount', 'amount_mismatch'].includes(w.code);
        return (
          <Flex
            key={idx}
            align="flex-start"
            gap={2}
            bg={isBlocking ? 'rgba(239, 68, 68, 0.10)' : 'rgba(239, 117, 71, 0.10)'}
            px={3}
            py={2}
            borderRadius="md"
            mb={1}
          >
            <WarningIcon
              color={isBlocking ? 'redesign.accent.red' : 'redesign.accent.expense'}
              boxSize={3.5}
              mt={0.5}
              flexShrink={0}
            />
            <Text fontSize="xs" color={isBlocking ? 'redesign.accent.red' : 'redesign.accent.expense'}>
              {w.message}
            </Text>
          </Flex>
        );
      })}
    </Box>
  );
};

/**
 * Split movement modal - two-phase UI:
 * Phase 1: Configure parts (amounts + optional overrides + transfer toggle)
 * Phase 2: Preview & confirm
 */
export default function SplitMovementModal({ isOpen, onClose, movement, onSplitComplete }) {
  const toast = useToast();
  const { t } = useTranslation('movements');
  const [parts, setParts] = useState([]);
  const [preview, setPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSplitting, setIsSplitting] = useState(false);
  const [accounts, setAccounts] = useState([]);

  const phase = preview ? 2 : 1;

  // Initialize parts when modal opens with a new movement
  const movementId = movement?.id;
  const movementAmount = movement?.amount || 0;
  const canToggleTransfer = movement?.type === 'Expenses' || movement?.type === 'Income';

  // Fetch accounts for the transfer toggle dropdown
  useEffect(() => {
    if (isOpen && canToggleTransfer) {
      fetchAccounts(true).then(setAccounts).catch(() => {});
    }
  }, [isOpen, canToggleTransfer]);

  // Reset state when movement changes
  const initParts = useCallback(() => {
    if (movementAmount > 0) {
      const half = Math.round((movementAmount / 2) * 100) / 100;
      const other = Math.round((movementAmount - half) * 100) / 100;
      setParts([
        { amount: half, note: '', isTransfer: false, toAccountId: '' },
        { amount: other, note: '', isTransfer: false, toAccountId: '' },
      ]);
    } else {
      setParts([
        { amount: 0, note: '', isTransfer: false, toAccountId: '' },
        { amount: 0, note: '', isTransfer: false, toAccountId: '' },
      ]);
    }
  }, [movementAmount]);

  // Re-initialize when modal opens
  const [lastMovementId, setLastMovementId] = useState(null);
  if (isOpen && movementId && movementId !== lastMovementId) {
    setLastMovementId(movementId);
    setPreview(null);
    initParts();
  }

  const totalParts = useMemo(() => {
    return parts.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  }, [parts]);

  const remaining = Math.round((movementAmount - totalParts) * 100) / 100;
  const isBalanced = Math.abs(remaining) < 0.01;

  // Check that all transfer parts have a to_account selected
  const transferPartsValid = parts.every(p => !p.isTransfer || p.toAccountId);

  const handlePartAmountChange = (index, value) => {
    setParts(prev => prev.map((p, i) =>
      i === index ? { ...p, amount: value === '' ? '' : parseFloat(value) || 0 } : p
    ));
  };

  const handlePartNoteChange = (index, value) => {
    setParts(prev => prev.map((p, i) =>
      i === index ? { ...p, note: value } : p
    ));
  };

  const handleTransferToggle = (index) => {
    setParts(prev => prev.map((p, i) =>
      i === index ? { ...p, isTransfer: !p.isTransfer, toAccountId: '' } : p
    ));
  };

  const handleToAccountChange = (index, value) => {
    setParts(prev => prev.map((p, i) =>
      i === index ? { ...p, toAccountId: value } : p
    ));
  };

  const handleAddPart = () => {
    setParts(prev => [...prev, {
      amount: Math.max(0, remaining),
      note: '',
      isTransfer: false,
      toAccountId: '',
    }]);
  };

  const handleRemovePart = (index) => {
    if (parts.length <= 2) return;
    setParts(prev => prev.filter((_, i) => i !== index));
  };

  const handleQuickSplit = (n) => {
    const base = Math.floor((movementAmount / n) * 100) / 100;
    const newParts = Array.from({ length: n }, (_, i) => ({
      amount: i < n - 1 ? base : Math.round((movementAmount - base * (n - 1)) * 100) / 100,
      note: '',
      isTransfer: false,
      toAccountId: '',
    }));
    setParts(newParts);
  };

  const buildPartsPayload = () => {
    return parts.map(p => {
      const part = { amount: parseFloat(p.amount) || 0 };
      if (p.note && p.note.trim() !== '') part.note = p.note.trim();
      if (p.isTransfer) {
        part.type = 'Transfer';
        part.from_account_id = movement.account_id;
        part.to_account_id = parseInt(p.toAccountId, 10);
      }
      return part;
    });
  };

  const handlePreview = async () => {
    if (!movementId) return;
    setIsLoadingPreview(true);
    try {
      const result = await splitMovement(movementId, buildPartsPayload(), true);
      setPreview(result);
    } catch (error) {
      toast({
        title: t('modals.split.toasts.errorTitle'),
        description: error.response?.data?.detail || t('modals.split.toasts.previewError'),
        status: 'error',
        duration: 4000,
      });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleConfirmSplit = async () => {
    if (!movementId) return;
    setIsSplitting(true);
    try {
      await splitMovement(movementId, buildPartsPayload(), false);
      toast({
        title: t('modals.split.toasts.successTitle'),
        description: t('modals.split.toasts.successDescription', { count: parts.length }),
        status: 'success',
        duration: 3000,
      });
      handleClose();
      onSplitComplete?.(movementId, movement?.date);
    } catch (error) {
      toast({
        title: t('modals.split.toasts.errorTitle'),
        description: error.response?.data?.detail || t('modals.split.toasts.splitError'),
        status: 'error',
        duration: 4000,
      });
    } finally {
      setIsSplitting(false);
    }
  };

  const handleClose = () => {
    setPreview(null);
    setIsLoadingPreview(false);
    setIsSplitting(false);
    setLastMovementId(null);
    onClose();
  };

  const handleBack = () => {
    setPreview(null);
  };

  if (!movement) return null;

  const isOriginalTransfer = movement.type === 'Transfer';
  const accountText = isOriginalTransfer
    ? `${movement.from_account?.name || '?'} → ${movement.to_account?.name || '?'}`
    : movement.account?.name || t('fallback.unknown');

  // Filter out the original account from the "to account" options
  const toAccountOptions = accounts.filter(a => a.id !== movement.account_id);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md" pb={2}>
          {phase === 1 ? t('modals.split.title.phase1') : t('modals.split.title.phase2')}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {/* Phase 1: Configure split parts */}
          {phase === 1 && (
            <Box>
              {/* Original movement summary */}
              <Box bg="redesign.bg.card" p={3} borderRadius="md" mb={4}>
                <Flex justify="space-between" align="center">
                  <Box>
                    <Text fontSize="sm" fontWeight="600">
                      {movement.note || movement.description || t('fallback.noNote')}
                    </Text>
                    <Text fontSize="xs" color="redesign.text.muted">
                      {accountText} &middot; {movement.date}
                    </Text>
                  </Box>
                  <Text
                    fontSize="sm"
                    fontWeight="700"
                    color={
                      movement.type === 'Income' ? 'redesign.accent.green' :
                      movement.type === 'Expenses' ? 'redesign.accent.red' :
                      'redesign.text'
                    }
                  >
                    {formatMoney(movement.amount, movement.currency)}
                  </Text>
                </Flex>
              </Box>

              {/* Quick split buttons */}
              <Flex gap={2} mb={3}>
                <Text fontSize="xs" color="redesign.text.muted" alignSelf="center">
                  {t('modals.split.quickSplit')}
                </Text>
                {[2, 3, 4].map(n => (
                  <Button
                    key={n}
                    size="xs"
                    variant="outline"
                    onClick={() => handleQuickSplit(n)}
                  >
                    &divide;{n}
                  </Button>
                ))}
              </Flex>

              {/* Parts editor */}
              <Box maxH="350px" overflowY="auto">
                {parts.map((part, idx) => (
                  <Box key={idx} mb={2}>
                    <Flex align="center" gap={2}>
                      <Text fontSize="xs" color="redesign.text.muted" w="20px" flexShrink={0} textAlign="right">
                        {idx + 1}.
                      </Text>
                      <InputGroup size="sm" flex={1}>
                        <InputLeftAddon fontSize="xs">{movement.currency}</InputLeftAddon>
                        <Input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={part.amount}
                          onChange={(e) => handlePartAmountChange(idx, e.target.value)}
                          fontSize="sm"
                          placeholder={t('modals.split.amountPlaceholder')}
                        />
                      </InputGroup>
                      <Input
                        size="sm"
                        flex={1}
                        value={part.note}
                        onChange={(e) => handlePartNoteChange(idx, e.target.value)}
                        placeholder={t('modals.split.notePlaceholder')}
                        fontSize="xs"
                      />
                      {canToggleTransfer && (
                        <Flex align="center" gap={1} flexShrink={0}>
                          <Switch
                            size="sm"
                            colorScheme="blue"
                            isChecked={part.isTransfer}
                            onChange={() => handleTransferToggle(idx)}
                          />
                          <Text fontSize="2xs" color={part.isTransfer ? 'redesign.accent.blue' : 'redesign.text.muted'} w="45px">
                            {t('modals.split.transferToggle')}
                          </Text>
                        </Flex>
                      )}
                      <IconButton
                        icon={<MinusIcon />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleRemovePart(idx)}
                        isDisabled={parts.length <= 2}
                        aria-label={t('modals.split.removePartAria')}
                      />
                    </Flex>
                    {/* To account picker when transfer is toggled on */}
                    {part.isTransfer && (
                      <Flex ml="28px" mt={1} align="center" gap={2}>
                        <Text fontSize="2xs" color="redesign.text.muted" flexShrink={0}>
                          {movement.account?.name || t('modals.split.accountFallback')} →
                        </Text>
                        <AccountPicker
                          value={part.toAccountId}
                          options={toAccountOptions}
                          onChange={(val) => handleToAccountChange(idx, val)}
                          placeholder={t('modals.split.toAccountPlaceholder')}
                        />
                      </Flex>
                    )}
                  </Box>
                ))}
              </Box>

              {/* Add part button */}
              <Button
                size="xs"
                variant="ghost"
                leftIcon={<AddIcon />}
                onClick={handleAddPart}
                mt={1}
                mb={3}
              >
                {t('modals.split.addPart')}
              </Button>

              {/* Remaining amount indicator */}
              <Flex
                justify="space-between"
                align="center"
                bg={isBalanced ? 'rgba(34, 197, 94, 0.10)' : 'rgba(239, 68, 68, 0.10)'}
                px={3}
                py={2}
                borderRadius="md"
              >
                <Text fontSize="xs" color={isBalanced ? 'redesign.accent.green' : 'redesign.accent.red'}>
                  {isBalanced ? t('modals.split.balanced') : t('modals.split.remaining')}
                </Text>
                <Text
                  fontSize="sm"
                  fontWeight="600"
                  color={isBalanced ? 'redesign.accent.green' : 'redesign.accent.red'}
                >
                  {isBalanced ? '0.00' : formatMoney(remaining, movement.currency)}
                </Text>
              </Flex>
            </Box>
          )}

          {/* Phase 2: Preview */}
          {phase === 2 && isLoadingPreview && (
            <Center py={8}>
              <Spinner size="md" color="redesign.text.muted" />
            </Center>
          )}

          {phase === 2 && !isLoadingPreview && preview && (
            <Box>
              {/* Warnings */}
              <WarningBanner warnings={preview.warnings} />

              {/* Resulting movements preview */}
              <Text fontSize="sm" fontWeight="600" mb={2}>
                {t('modals.split.resultingMovements', { count: preview.resulting_movements.length })}
              </Text>
              <Box maxH="300px" overflowY="auto">
                {preview.resulting_movements.map((mov, idx) => (
                  <MovementRow
                    key={idx}
                    movement={mov}
                    index={idx}
                    highlight={idx === 0}
                  />
                ))}
              </Box>

              {/* Group info */}
              {preview.group_to_create && (
                <Flex gap={2} mt={2}>
                  <Badge colorScheme="purple" variant="subtle" fontSize="2xs">
                    {t('modals.split.newGroupBadge')}
                  </Badge>
                </Flex>
              )}

              <Divider my={3} />

              {/* Irreversible warning */}
              <Flex align="center" gap={2} bg="rgba(239, 68, 68, 0.10)" px={3} py={2} borderRadius="md">
                <WarningIcon color="redesign.accent.red" boxSize={4} flexShrink={0} />
                <Text fontSize="sm" color="redesign.accent.red" fontWeight="500">
                  {t('modals.split.warning')}
                </Text>
              </Flex>
            </Box>
          )}
        </ModalBody>

        <ModalFooter gap={2}>
          {phase === 1 && (
            <>
              <Button size="sm" variant="ghost" onClick={handleClose}>
                {t('modals.split.buttons.cancel')}
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={handlePreview}
                isLoading={isLoadingPreview}
                isDisabled={!isBalanced || parts.length < 2 || !transferPartsValid}
              >
                {t('modals.split.buttons.preview')}
              </Button>
            </>
          )}
          {phase === 2 && (
            <>
              <Button size="sm" variant="ghost" onClick={handleBack} isDisabled={isSplitting}>
                {t('modals.split.buttons.back')}
              </Button>
              <Button
                size="sm"
                colorScheme="red"
                onClick={handleConfirmSplit}
                isLoading={isSplitting}
                isDisabled={!preview?.can_split}
              >
                {t('modals.split.buttons.confirm')}
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
